import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Animated,
  Easing,
  FlatList,
  TextInput,
  ActivityIndicator,
  ScrollView,
  Platform,
  Modal,
  TouchableWithoutFeedback,
  Dimensions,
  useWindowDimensions,
  KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { PhoneInput } from './design-system';
import { parseInternationalPhoneNumber, getCountryFromPhone } from '../utils/phoneNumber';
import { getImageUrlSync, getApiBaseURL } from '../utils/imageProxy';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { InsetModal } from './InsetModal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { withAlpha } from '../theme';
import { firstText } from '../utils/contactIdentifiers';

export type ContactSelectorMode = 'view' | 'edit' | 'admin';
export type ContactSelectorVariant = 'simple' | 'minor' | 'inline' | 'big';

interface Contact {
  id: string;
  _id?: string;
  name: string;
  phoneNumber?: string;
  phone?: string;
  email?: string;
  address?: string;
  profilePictureUrl?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    address?: string;
  };
  [key: string]: any;
}

interface ContactSelectorProps {
  mode: ContactSelectorMode;
  variant: ContactSelectorVariant;
  contactId?: string;
  contact?: Contact | null;
  onContactSelect?: (contact: Contact) => void;
  onContactUpdate?: (contactId: string, updates: Partial<Contact>) => Promise<void>;
  onAddNewContact?: () => void;
  showPreviousEvents?: boolean;
  showPreviousOrders?: boolean;
  style?: any;
  disabled?: boolean;
}

export const ContactSelector: React.FC<ContactSelectorProps> = ({
  mode,
  variant,
  contactId,
  contact: initialContact,
  onContactSelect,
  onContactUpdate,
  onAddNewContact,
  showPreviousEvents = false,
  showPreviousOrders = false,
  style,
  disabled = false,
}) => {
  const { currentBusiness } = useAppStore();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(initialContact || null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingContact, setEditingContact] = useState<Partial<Contact>>({});
  const [updating, setUpdating] = useState(false);
  const [previousEvents, setPreviousEvents] = useState<any[]>([]);
  const [previousOrders, setPreviousOrders] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneCountryCode, setPhoneCountryCode] = useState<string | undefined>(undefined);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);

  // Load API base URL for image proxy
  useEffect(() => {
    const loadApiBaseURL = async () => {
      const baseURL = await getApiBaseURL();
      setApiBaseURL(baseURL);
    };
    loadApiBaseURL();
  }, []);

  // Animations
  const dropdownHeight = useRef(new Animated.Value(0)).current;
  const dropdownOpacity = useRef(new Animated.Value(0)).current;
  const nameSlideX = useRef(new Animated.Value(20)).current;
  const nameOpacity = useRef(new Animated.Value(0)).current;
  const detailsHeight = useRef(new Animated.Value(0)).current;
  const detailsOpacity = useRef(new Animated.Value(0)).current;

  // Load contact if contactId provided
  useEffect(() => {
    if (contactId && !initialContact) {
      loadContact(contactId);
    }
  }, [contactId]);

  // Sync selectedContact when initialContact changes (e.g., when order is reopened)
  useEffect(() => {
    if (initialContact) {
      // Ensure profilePictureUrl is preserved from initialContact
      const profilePictureUrl = initialContact.profilePictureUrl || 
                                (initialContact as any)?.parentContact?.profile?.avatar ||
                                (initialContact as any)?.profile?.avatar;
      
      const completeContact: Contact = {
        ...initialContact,
        profilePictureUrl: profilePictureUrl || initialContact.profilePictureUrl,
      };
      
      setSelectedContact(completeContact);
    }
  }, [initialContact]);

  // Load contacts for dropdown
  useEffect(() => {
    if ((mode === 'edit' || mode === 'admin') && showDropdown) {
      loadContacts();
    }
  }, [showDropdown, mode]);

  // Load history when contact is selected
  // For big mode, always load history to show statistics
  useEffect(() => {
    if (selectedContact && (variant === 'big' || showPreviousEvents || showPreviousOrders)) {
      loadHistory();
    }
  }, [selectedContact, variant, showPreviousEvents, showPreviousOrders]);

  // Animate dropdown (all useNativeDriver: false — height + opacity on same View; mixing drivers causes mobile crash)
  useEffect(() => {
    if (showDropdown) {
      Animated.parallel([
        Animated.spring(dropdownHeight, {
          toValue: 1,
          tension: 50,
          friction: 8,
          useNativeDriver: false,
        }),
        Animated.timing(dropdownOpacity, {
          toValue: 1,
          duration: 250,
          easing: Easing.out(Easing.ease),
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(dropdownHeight, {
          toValue: 0,
          duration: 200,
          easing: Easing.in(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(dropdownOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [showDropdown]);

  // Animate name slide when contact selected (detailsHeight + detailsOpacity on same View → useNativeDriver: false for both)
  useEffect(() => {
    if (selectedContact) {
      Animated.parallel([
        Animated.spring(nameSlideX, {
          toValue: 0,
          tension: 50,
          friction: 7,
          useNativeDriver: true,
        }),
        Animated.timing(nameOpacity, {
          toValue: 1,
          duration: 300,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.spring(detailsHeight, {
          toValue: 1,
          tension: 40,
          friction: 7,
          useNativeDriver: false,
        }),
        Animated.timing(detailsOpacity, {
          toValue: 1,
          duration: 400,
          delay: 150,
          easing: Easing.out(Easing.ease),
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      nameSlideX.setValue(20);
      nameOpacity.setValue(0);
      detailsHeight.setValue(0);
      detailsOpacity.setValue(0);
    }
  }, [selectedContact]);

  const loadContact = async (id: string) => {
    try {
      // Load parent contact by ID
      const response = await apiService.getParentContactById(id);
      if (response.statusCode === 1000 && response.data) {
        const parentContact = response.data;
        // Map parent contact to Contact format
        const contact: Contact = {
          id: parentContact._id || parentContact.id,
          _id: parentContact._id || parentContact.id,
          name: parentContact.profile?.name || '',
          phoneNumber: parentContact.profile?.phone || parentContact.identifiers?.find((id: any) => id.type === 'phone')?.value,
          phone: parentContact.profile?.phone || parentContact.identifiers?.find((id: any) => id.type === 'phone')?.value,
          email: parentContact.profile?.email || parentContact.identifiers?.find((id: any) => id.type === 'email')?.value,
          address: parentContact.profile?.location?.address,
          location: parentContact.profile?.location,
          profilePictureUrl: parentContact.profile?.avatar,
          ...parentContact,
        };
        setSelectedContact(contact);
      }
    } catch (error) {
      console.error('Error loading contact:', error);
    }
  };

  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      // Use parent contacts API with search query if available
      const query = searchQuery.trim();
      const response = await apiService.getParentContacts({
        limit: 1000,
        offset: 0,
        ...(query ? { search: query } : {}),
      });
      
      console.log('[ContactSelector] Load contacts response:', response);
      
      if (response.statusCode === 1000 && response.data) {
        const parentContacts = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.contacts)
          ? response.data.contacts
          : [];
        console.log('[ContactSelector] Found parent contacts:', parentContacts.length);
        
        // Map parent contacts to Contact format
        const mappedContacts: Contact[] = parentContacts.map((parentContact: any) => {
          // Handle identifiers - can be array or object
          let phoneValue = parentContact.profile?.phone;
          let emailValue = parentContact.profile?.email;
          
          if (!phoneValue && parentContact.identifiers) {
            if (Array.isArray(parentContact.identifiers)) {
              const phoneId = parentContact.identifiers.find((id: any) => 
                id.type === 'phone' || id.type === 'whatsapp'
              );
              phoneValue = phoneId?.value;
            } else if (typeof parentContact.identifiers === 'object') {
              phoneValue = firstText(parentContact.identifiers.phone) || firstText(parentContact.identifiers.whatsapp);
            }
          }
          
          if (!emailValue && parentContact.identifiers) {
            if (Array.isArray(parentContact.identifiers)) {
              const emailId = parentContact.identifiers.find((id: any) => id.type === 'email');
              emailValue = emailId?.value;
            } else if (typeof parentContact.identifiers === 'object') {
              emailValue = firstText(parentContact.identifiers.email);
            }
          }
          
          // The raw contact first, then the normalized fields — spreading it
          // last let list-valued identifiers overwrite phone/email.
          return {
            ...parentContact,
            id: parentContact._id || parentContact.id,
            _id: parentContact._id || parentContact.id,
            name: String(parentContact.profile?.name || parentContact.name || ''),
            phoneNumber: firstText(phoneValue),
            phone: firstText(phoneValue),
            email: firstText(emailValue),
            address: parentContact.profile?.location?.address,
            location: parentContact.profile?.location,
            profilePictureUrl: parentContact.profile?.avatar,
          };
        });
        setContacts(mappedContacts);
        console.log('[ContactSelector] Mapped contacts:', mappedContacts.length);
      } else {
        console.warn('[ContactSelector] No contacts found or error:', response);
        setContacts([]);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadHistory = async () => {
    if (!selectedContact || !currentBusiness?._id) return;
    
    try {
      setLoadingHistory(true);
      
      const phone = selectedContact.phoneNumber || selectedContact.phone;
      
      // Load events (always for big mode, or when showPreviousEvents is true)
      if (variant === 'big' || showPreviousEvents) {
        try {
          if (phone && currentBusiness?._id) {
            const response = await apiService.getEventsByPhone(phone, currentBusiness._id);
            if (response.statusCode === 1000 && response.data) {
              setPreviousEvents(Array.isArray(response.data) ? response.data : []);
            }
          }
        } catch (error) {
          console.error('Error loading events:', error);
        }
      }
      
      // Load orders (always for big mode, or when showPreviousOrders is true)
      if (variant === 'big' || showPreviousOrders) {
        try {
          if (phone) {
            const response = await apiService.getOrders({
              search: phone,
              limit: 50,
            });
            if (response.statusCode === 1000 && response.data) {
              setPreviousOrders(Array.isArray(response.data) ? response.data : []);
            }
          }
        } catch (error) {
          console.error('Error loading orders:', error);
        }
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleContactSelect = (contact: Contact) => {
    // Ensure profilePictureUrl is preserved from the contact object
    // It might be in contact.profilePictureUrl or contact.parentContact?.profile?.avatar
    const profilePictureUrl = contact.profilePictureUrl || 
                              (contact as any)?.parentContact?.profile?.avatar ||
                              (contact as any)?.profile?.avatar;
    
    // Create a complete contact object with all properties preserved
    const completeContact: Contact = {
      ...contact,
      profilePictureUrl: profilePictureUrl || contact.profilePictureUrl,
    };
    
    console.log('[ContactSelector] Selecting contact:', {
      name: completeContact.name,
      profilePictureUrl: completeContact.profilePictureUrl,
      hasAvatar: !!completeContact.profilePictureUrl,
    });
    
    setSelectedContact(completeContact);
    
    // Parse phone number to extract national number (without country code) and country
    const rawPhone = contact.phoneNumber || contact.phone || '';
    if (rawPhone) {
      const parsed = parseInternationalPhoneNumber(rawPhone);
      if (parsed && parsed.isValid) {
        // Set only the national number (without country code)
        setPhoneNumber(parsed.nationalNumber);
        // Store country code for PhoneInput initialCountry
        setPhoneCountryCode(parsed.country);
      } else {
        // Fallback: if parsing fails, try to remove + and country code manually
        const cleaned = rawPhone.replace(/^\+/, '').replace(/^\d{1,4}/, '');
        setPhoneNumber(cleaned || rawPhone);
        // Try to detect country from original number
        const countryFromPhone = getCountryFromPhone(rawPhone);
        setPhoneCountryCode(countryFromPhone);
      }
    } else {
      setPhoneNumber('');
      setPhoneCountryCode(undefined);
    }
    
    setShowDropdown(false);
    setSearchQuery('');
    onContactSelect?.(completeContact);
  };

  const handleRemoveContact = () => {
    setSelectedContact(null);
    setPhoneNumber('');
    setPhoneCountryCode(undefined);
    setShowDropdown(false);
    setSearchQuery('');
    // Call onContactSelect with null to indicate removal
    onContactSelect?.(null as any);
  };

  const handleAddNewContact = () => {
    setShowDropdown(false);
    onAddNewContact?.();
  };

  // Update phone when contact changes
  useEffect(() => {
    if (selectedContact) {
      // Parse phone number to extract national number (without country code) and country
      const rawPhone = firstText(selectedContact.phoneNumber) || firstText(selectedContact.phone);
      if (rawPhone) {
        const parsed = parseInternationalPhoneNumber(rawPhone);
        if (parsed && parsed.isValid) {
          // Set only the national number (without country code)
          setPhoneNumber(parsed.nationalNumber);
          // Store country code for PhoneInput initialCountry
          setPhoneCountryCode(parsed.country);
        } else {
          // Fallback: if parsing fails, try to remove + and country code manually
          const cleaned = rawPhone.replace(/^\+/, '').replace(/^\d{1,4}/, '');
          setPhoneNumber(cleaned || rawPhone);
          // Try to detect country from original number
          const countryFromPhone = getCountryFromPhone(rawPhone);
          setPhoneCountryCode(countryFromPhone);
        }
      } else {
        setPhoneNumber('');
        setPhoneCountryCode(undefined);
      }
    } else {
      setPhoneNumber('');
      setPhoneCountryCode(undefined);
    }
  }, [selectedContact]);

  const handleEdit = () => {
    if (selectedContact) {
      setEditingContact({ ...selectedContact });
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (!selectedContact || !onContactUpdate) return;
    
    try {
      setUpdating(true);
      await onContactUpdate(selectedContact.id || selectedContact._id || '', editingContact);
      setSelectedContact({ ...selectedContact, ...editingContact });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating contact:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingContact({});
  };

  const getContactInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const filteredContacts = searchQuery.trim()
    ? contacts.filter(contact => {
        const query = searchQuery.toLowerCase();
        const name = String(contact.name || '').toLowerCase();
        const phone = String(firstText(contact.phoneNumber) || firstText(contact.phone)).toLowerCase();
        const email = String(firstText(contact.email)).toLowerCase();
        return name.includes(query) || phone.includes(query) || email.includes(query);
      })
    : contacts;

  // Render based on variant
  if (variant === 'inline') {
    return <InlineView contact={selectedContact} mode={mode} onEdit={handleEdit} apiBaseURL={apiBaseURL} />;
  }

  if (variant === 'minor') {
    return (
      <MinorView
        contact={selectedContact}
        mode={mode}
        showDropdown={showDropdown}
        onToggleDropdown={() => !disabled && setShowDropdown(!showDropdown)}
        onContactSelect={handleContactSelect}
        onAddNewContact={handleAddNewContact}
        phoneNumber={phoneNumber}
        onPhoneNumberChange={(text) => {
          setPhoneNumber(text);
          if (selectedContact) {
            setSelectedContact({ ...selectedContact, phoneNumber: text, phone: text });
          }
        }}
        phoneCountryCode={phoneCountryCode}
        filteredContacts={filteredContacts}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loadingContacts={loadingContacts}
        dropdownHeight={dropdownHeight}
        dropdownOpacity={dropdownOpacity}
        nameSlideX={nameSlideX}
        nameOpacity={nameOpacity}
        detailsHeight={detailsHeight}
        detailsOpacity={detailsOpacity}
        getContactInitials={getContactInitials}
        apiBaseURL={apiBaseURL}
        style={style}
        disabled={disabled}
        onRemoveContact={handleRemoveContact}
      />
    );
  }

  if (variant === 'big') {
    return (
      <BigView
        contact={selectedContact}
        mode={mode}
        isEditing={isEditing}
        editingContact={editingContact}
        onEditChange={setEditingContact}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        updating={updating}
        showDropdown={showDropdown}
        onToggleDropdown={() => !disabled && setShowDropdown(!showDropdown)}
        onContactSelect={handleContactSelect}
        onAddNewContact={handleAddNewContact}
        onRemoveContact={handleRemoveContact}
        phoneNumber={phoneNumber}
        onPhoneNumberChange={(text) => {
          setPhoneNumber(text);
          if (selectedContact) {
            setSelectedContact({ ...selectedContact, phoneNumber: text, phone: text });
          }
        }}
        phoneCountryCode={phoneCountryCode}
        filteredContacts={filteredContacts}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loadingContacts={loadingContacts}
        previousEvents={previousEvents}
        previousOrders={previousOrders}
        loadingHistory={loadingHistory}
        showPreviousEvents={showPreviousEvents}
        showPreviousOrders={showPreviousOrders}
        dropdownHeight={dropdownHeight}
        dropdownOpacity={dropdownOpacity}
        nameSlideX={nameSlideX}
        nameOpacity={nameOpacity}
        detailsHeight={detailsHeight}
        detailsOpacity={detailsOpacity}
        getContactInitials={getContactInitials}
        apiBaseURL={apiBaseURL}
        style={style}
        disabled={disabled}
        onRemoveContact={handleRemoveContact}
      />
    );
  }

  // Simple variant (default)
  return (
    <SimpleView
      contact={selectedContact}
      mode={mode}
      showDropdown={showDropdown}
      onToggleDropdown={() => !disabled && setShowDropdown(!showDropdown)}
      onContactSelect={handleContactSelect}
      onAddNewContact={handleAddNewContact}
      phoneNumber={phoneNumber}
      onPhoneNumberChange={(text) => {
        setPhoneNumber(text);
        if (selectedContact) {
          const updated = { ...selectedContact, phoneNumber: text, phone: text };
          setSelectedContact(updated);
          onContactSelect?.(updated);
        }
      }}
      phoneCountryCode={phoneCountryCode}
      filteredContacts={filteredContacts}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      loadingContacts={loadingContacts}
      dropdownHeight={dropdownHeight}
      dropdownOpacity={dropdownOpacity}
      nameSlideX={nameSlideX}
      nameOpacity={nameOpacity}
      detailsHeight={detailsHeight}
      detailsOpacity={detailsOpacity}
      getContactInitials={getContactInitials}
      apiBaseURL={apiBaseURL}
      style={style}
      disabled={disabled}
      onRemoveContact={handleRemoveContact}
    />
  );
};

// Simple View Component
interface SimpleViewProps {
  contact: Contact | null;
  mode: ContactSelectorMode;
  showDropdown: boolean;
  onToggleDropdown: () => void;
  onContactSelect: (contact: Contact) => void;
  onAddNewContact?: () => void;
  onRemoveContact?: () => void;
  phoneNumber: string;
  onPhoneNumberChange: (text: string) => void;
  phoneCountryCode?: string;
  filteredContacts: Contact[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  loadingContacts: boolean;
  dropdownHeight: Animated.Value;
  dropdownOpacity: Animated.Value;
  nameSlideX: Animated.Value;
  nameOpacity: Animated.Value;
  detailsHeight: Animated.Value;
  detailsOpacity: Animated.Value;
  getContactInitials: (name: string) => string;
  apiBaseURL?: string | null;
  style?: any;
  disabled: boolean;
}

const SimpleView: React.FC<SimpleViewProps> = ({
  contact,
  mode,
  showDropdown,
  onToggleDropdown,
  onContactSelect,
  onAddNewContact,
  onRemoveContact,
  phoneNumber,
  onPhoneNumberChange,
  phoneCountryCode,
  filteredContacts,
  searchQuery,
  onSearchChange,
  loadingContacts,
  dropdownHeight,
  dropdownOpacity,
  nameSlideX,
  nameOpacity,
  detailsHeight,
  detailsOpacity,
  getContactInitials,
  apiBaseURL,
  style,
  disabled,
}) => {

  const styles = useThemedStyles(getStyles);
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  // Phones (native app + mobile browsers): the list opens as a bottom sheet.
  // The inline dropdown hangs below the row outside its parent's bounds —
  // on iOS/Android taps there (e.g. on its search box) never reach it, and
  // the keyboard opening reflowed the page, so the dropdown closed as soon
  // as its search box was focused.
  const useSheet = Platform.OS !== 'web' || windowWidth < 768;

  return (
    <View 
      style={[styles.simpleContainer, style]}
    >
      {/* Contact Selector - Avatar + Name/Phone Column */}
      <View style={styles.contactRow}>
        {/* Avatar - Left side */}
        <View style={styles.contactAvatar}>
          {contact ? (
            contact.profilePictureUrl ? (
              <Image 
                source={{ uri: getImageUrlSync(contact.profilePictureUrl, apiBaseURL) }} 
                style={styles.contactAvatarImage}
                onError={(e) => {
                  console.error('❌ [ContactSelector] Failed to load contact avatar:', contact.profilePictureUrl, e.nativeEvent.error);
                }}
              />
            ) : (
              <View style={[styles.contactAvatarImage, styles.contactAvatarPlaceholder]}>
                <Text style={styles.contactAvatarText}>
                  {getContactInitials(contact.name)}
                </Text>
              </View>
            )
          ) : (
            <View style={[styles.contactAvatarImage, styles.contactAvatarPlaceholder]}>
              <Ionicons name="person-outline" size={18} color={colors.textSecondary} />
            </View>
          )}
        </View>

        {/* Name and Phone - Right side column */}
        <View style={styles.contactInfoColumn}>
          {contact ? (
            <TouchableOpacity
              style={styles.contactInfoButton}
              onPress={mode === 'edit' ? onToggleDropdown : undefined}
              activeOpacity={mode === 'edit' ? 0.7 : 1}
              disabled={disabled || mode === 'view'}
            >
              <View style={styles.contactInfoContent}>
                <Animated.View 
                  style={[
                    styles.contactNameWrapper,
                    {
                      opacity: nameOpacity,
                      transform: [{ translateX: nameSlideX }],
                    }
                  ]}
                >
                  <Text style={styles.contactName} numberOfLines={1}>
                    {contact.name}
                  </Text>
                </Animated.View>
                
                {/* Phone Input - Small, inline */}
                <Animated.View
                  style={[
                    styles.phoneInputContainer,
                    {
                      maxHeight: detailsHeight.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, 50],
                      }),
                      opacity: detailsOpacity,
                    }
                  ]}
                >
                  <PhoneInput
                    value={phoneNumber}
                    onChangeText={onPhoneNumberChange}
                    placeholder="Phone"
                    style={styles.phoneInput}
                    containerStyle={styles.phoneInputContainerStyle}
                    size="sm"
                    initialCountry={phoneCountryCode || getCountryFromPhone(phoneNumber) || undefined}
                    mode={mode}
                  />
                </Animated.View>
              </View>
              {mode === 'edit' && (
                <View style={styles.contactActions}>
                  {contact && onRemoveContact && (
                    <TouchableOpacity
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        onRemoveContact();
                      }}
                      style={styles.removeContactButton}
                      activeOpacity={0.7}
                      {...(Platform.OS === 'web' && {
                        // @ts-ignore - web-specific prop
                        onClick: (e: any) => {
                          if (e?.stopPropagation) e.stopPropagation();
                          if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
                          if (e?.preventDefault) e.preventDefault();
                        },
                        onMouseDown: (e: any) => {
                          if (e?.stopPropagation) e.stopPropagation();
                          if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
                        },
                      })}
                    >
                      <Ionicons 
                        name="close-circle" 
                        size={18} 
                        color={colors.error || colors.danger || '#dc3545'} 
                      />
                    </TouchableOpacity>
                  )}
                  <Ionicons 
                    name={showDropdown ? "chevron-up" : "chevron-down"} 
                    size={14} 
                    color={colors.textSecondary} 
                    style={styles.contactChevron}
                  />
                </View>
              )}
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={styles.contactNameButton}
              onPress={mode === 'edit' ? onToggleDropdown : undefined}
              activeOpacity={mode === 'edit' ? 0.7 : 1}
              disabled={disabled || mode === 'view'}
            >
              <Text style={styles.contactPlaceholder}>Select a contact...</Text>
              {mode === 'edit' && (
                <Ionicons 
                  name={showDropdown ? "chevron-up" : "chevron-down"} 
                  size={14} 
                  color={colors.textSecondary} 
                  style={styles.contactChevron}
                />
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Dropdown - Centered below contact row */}
      {mode === 'edit' && !useSheet && (
        <View style={styles.dropdownWrapper} pointerEvents={showDropdown ? 'auto' : 'none'}>
          <Animated.View
            style={[
              styles.dropdownContainer,
              {
                opacity: dropdownOpacity,
                height: dropdownHeight.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 500],
                }),
                display: showDropdown ? 'flex' : 'none',
                transform: [{
                  translateY: dropdownHeight.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-10, 0],
                  })
                }],
              }
            ]}
            data-dropdown-list="true"
            {...(Platform.OS === 'web' && {
              // @ts-ignore - web-specific prop
              onClick: (e: any) => {
                e?.stopPropagation?.();
              },
              onMouseDown: (e: any) => {
                e?.stopPropagation?.();
              },
            })}
          >
            <View style={styles.dropdownContent}>
              <View style={styles.contactSearchContainer}>
                <Ionicons name="search" size={18} color={colors.textSecondary} style={styles.contactSearchIcon} />
                <TextInput
                  style={styles.contactSearchInput}
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChangeText={onSearchChange}
                  placeholderTextColor={colors.textSecondary}
                />
                {onAddNewContact && (
                  <TouchableOpacity
                    style={styles.addContactButton}
                    onPress={onAddNewContact}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="add" size={20} color={colors.primary} />
                  </TouchableOpacity>
                )}
              </View>

              <ScrollView
                nestedScrollEnabled
                style={{ maxHeight: 500 }}
                contentContainerStyle={{ paddingBottom: spacing.xs }}
                showsVerticalScrollIndicator={true}
                scrollEnabled={true}
                {...(Platform.OS === 'web' && {
                  // @ts-ignore - web-specific prop
                  onClick: (e: any) => {
                    e?.stopPropagation?.();
                  },
                  onMouseDown: (e: any) => {
                    e?.stopPropagation?.();
                  },
                })}
              >
                {filteredContacts.map((item) => (
                  <TouchableOpacity
                    key={item.id || item._id || `${item.name}-${item.phoneNumber}`}
                    style={styles.contactOption}
                    onPress={() => onContactSelect(item)}
                    activeOpacity={0.7}
                    {...(Platform.OS === 'web' && {
                      // @ts-ignore - web-specific prop
                      onClick: (e: any) => {
                        // Stop all event propagation immediately to prevent document listener from closing dropdown
                        if (e?.stopPropagation) e.stopPropagation();
                        if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
                        if (e?.preventDefault) e.preventDefault();
                        // Also handle native event
                        if (e?.nativeEvent) {
                          if (e.nativeEvent.stopPropagation) e.nativeEvent.stopPropagation();
                          if (e.nativeEvent.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
                          if (e.nativeEvent.preventDefault) e.nativeEvent.preventDefault();
                        }
                      },
                      onMouseDown: (e: any) => {
                        // Also stop mousedown to prevent document listener from firing
                        if (e?.stopPropagation) e.stopPropagation();
                        if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
                      },
                    })}
                  >
                    <View style={styles.contactOptionAvatar}>
                      {item.profilePictureUrl ? (
                        <Image 
                          source={{ uri: getImageUrlSync(item.profilePictureUrl, apiBaseURL) }} 
                          style={styles.contactOptionAvatarImage}
                          onError={(e) => {
                            console.error('❌ [ContactSelector] Failed to load dropdown avatar:', item.profilePictureUrl, e.nativeEvent.error);
                          }}
                        />
                      ) : (
                        <View style={[styles.contactOptionAvatarImage, styles.contactOptionAvatarPlaceholder]}>
                          <Text style={styles.contactOptionAvatarText}>
                            {getContactInitials(item.name)}
                          </Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.contactOptionInfo}>
                      <Text style={styles.contactOptionName}>{item.name}</Text>
                      {(item.phoneNumber || item.phone) && (
                        <Text style={styles.contactOptionPhone}>
                          {item.phoneNumber || item.phone}
                        </Text>
                      )}
                    </View>
                    {contact?.id === item.id && (
                      <Ionicons name="checkmark-circle" size={20} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                ))}
                {filteredContacts.length === 0 && (
                  <View style={styles.contactEmpty}>
                    <Text style={styles.contactEmptyText}>
                      {loadingContacts ? 'Loading contacts...' : 'No contacts found'}
                    </Text>
                  </View>
                )}
              </ScrollView>
            </View>
          </Animated.View>
        </View>
        )}

      {mode === 'edit' && useSheet && (
        <InsetModal visible={showDropdown} transparent animationType="slide" onRequestClose={onToggleDropdown}>
          <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : Platform.OS === 'android' ? 'height' : undefined}>
            <TouchableWithoutFeedback onPress={onToggleDropdown}>
              <View style={{ flex: 1, backgroundColor: colors.background.overlay as any }} />
            </TouchableWithoutFeedback>
            {/* Fixed height (not content-sized): with few search results a
                content-sized sheet shrank down behind the keyboard and looked
                closed. This keeps the search box near the top of the screen,
                above the keyboard, whatever the result count. */}
            <View
              style={{
                height: '78%',
                backgroundColor: colors.background.surface as any,
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                paddingTop: spacing.sm,
                paddingBottom: Math.max(insets.bottom, spacing.sm),
              }}
            >
              <View style={{ alignSelf: 'center', width: 40, height: 4, borderRadius: 2, backgroundColor: colors.border as any, marginBottom: spacing.sm }} />
              <View style={[styles.contactSearchContainer, { marginHorizontal: spacing.md }]}>
                <Ionicons name="search" size={18} color={colors.textSecondary} style={styles.contactSearchIcon} />
                <TextInput
                  style={styles.contactSearchInput}
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChangeText={onSearchChange}
                  placeholderTextColor={colors.textSecondary}
                  autoCorrect={false}
                />
                {onAddNewContact && (
                  <TouchableOpacity style={styles.addContactButton} onPress={onAddNewContact} activeOpacity={0.7}>
                    <Ionicons name="add" size={20} color={colors.primary} />
                  </TouchableOpacity>
                )}
              </View>
              <ScrollView style={{ flex: 1 }} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag" contentContainerStyle={{ paddingBottom: spacing.sm }}>
                {filteredContacts.map((item) => (
                  <TouchableOpacity
                    key={item.id || item._id || `${item.name}-${item.phoneNumber}`}
                    style={styles.contactOption}
                    onPress={() => onContactSelect(item)}
                    activeOpacity={0.7}
                  >
                    <View style={styles.contactOptionAvatar}>
                      {item.profilePictureUrl ? (
                        <Image source={{ uri: getImageUrlSync(item.profilePictureUrl, apiBaseURL) }} style={styles.contactOptionAvatarImage as any} />
                      ) : (
                        <View style={[styles.contactOptionAvatarImage, styles.contactOptionAvatarPlaceholder]}>
                          <Text style={styles.contactOptionAvatarText}>{getContactInitials(item.name)}</Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.contactOptionInfo}>
                      <Text style={styles.contactOptionName}>{item.name}</Text>
                      {(item.phoneNumber || item.phone) && (
                        <Text style={styles.contactOptionPhone}>{item.phoneNumber || item.phone}</Text>
                      )}
                    </View>
                    {contact?.id === item.id && <Ionicons name="checkmark-circle" size={20} color={colors.primary} />}
                  </TouchableOpacity>
                ))}
                {filteredContacts.length === 0 && (
                  <View style={styles.contactEmpty}>
                    <Text style={styles.contactEmptyText}>{loadingContacts ? 'Loading contacts...' : 'No contacts found'}</Text>
                  </View>
                )}
              </ScrollView>
            </View>
          </KeyboardAvoidingView>
        </InsetModal>
      )}
    </View>
  );
};

// Minor View Component (smaller version for cards)
interface MinorViewProps extends SimpleViewProps {
  onAddNewContact?: () => void;
}

const MinorView: React.FC<MinorViewProps> = (props) => {
  // Apply smaller styles for minor variant
  return <SimpleView {...props} />;
};

// Inline View Component
interface InlineViewProps {
  contact: Contact | null;
  mode: ContactSelectorMode;
  onEdit?: () => void;
  apiBaseURL?: string | null;
}

const InlineView: React.FC<InlineViewProps> = ({ contact, mode, onEdit, apiBaseURL }) => {
  const styles = useThemedStyles(getStyles);
  if (!contact) return null;

  return (
    <View style={styles.inlineContainer}>
      {contact.profilePictureUrl ? (
        <Image 
          source={{ uri: getImageUrlSync(contact.profilePictureUrl, apiBaseURL) }} 
          style={styles.inlineAvatar}
          onError={(e) => {
            console.error('❌ [ContactSelector] Failed to load inline avatar:', contact.profilePictureUrl, e.nativeEvent.error);
          }}
        />
      ) : (
        <View style={[styles.inlineAvatar, styles.inlineAvatarPlaceholder]}>
          <Text style={styles.inlineAvatarText}>
            {contact.name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase()}
          </Text>
        </View>
      )}
      <Text style={styles.inlineName}>{contact.name}</Text>
      {contact.phoneNumber || contact.phone ? (
        <Text style={styles.inlinePhone}>{contact.phoneNumber || contact.phone}</Text>
      ) : null}
      {mode === 'admin' && onEdit && (
        <TouchableOpacity onPress={onEdit} style={styles.inlineEditButton}>
          <Ionicons name="create-outline" size={16} color={colors.primary} />
        </TouchableOpacity>
      )}
    </View>
  );
};

// Big View Component
interface BigViewProps extends SimpleViewProps {
  isEditing: boolean;
  editingContact: Partial<Contact>;
  onEditChange: (updates: Partial<Contact>) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  updating: boolean;
  previousEvents: any[];
  previousOrders: any[];
  loadingHistory: boolean;
  showPreviousEvents?: boolean;
  showPreviousOrders?: boolean;
}

const BigView: React.FC<BigViewProps> = ({
  contact,
  mode,
  isEditing,
  editingContact,
  onEditChange,
  onEdit,
  onSave,
  onCancel,
  updating,
  previousEvents,
  previousOrders,
  loadingHistory,
  showPreviousEvents = false,
  showPreviousOrders = false,
  onAddNewContact,
  ...simpleProps
}) => {
  const styles = useThemedStyles(getStyles);
  // Helper function to get social media items
  const getSocialMediaItems = (): Array<{ platform: string; value: string; icon: string }> => {
    const socialItems: Array<{ platform: string; value: string; icon: string }> = [];
    const contactAny = contact as any;

    // Check direct contact fields
    if (contactAny.whatsapp || contactAny.whatsappNumber) {
      socialItems.push({
        platform: 'WhatsApp',
        value: contactAny.whatsapp || contactAny.whatsappNumber,
        icon: 'logo-whatsapp',
      });
    }
    if (contactAny.facebookId || contactAny.facebook) {
      socialItems.push({
        platform: 'Facebook',
        value: contactAny.facebookId || contactAny.facebook,
        icon: 'logo-facebook',
      });
    }
    if (contactAny.instagram || contactAny.instagramUsername) {
      socialItems.push({
        platform: 'Instagram',
        value: contactAny.instagram || contactAny.instagramUsername,
        icon: 'logo-instagram',
      });
    }
    if (contactAny.twitter || contactAny.twitterUsername) {
      socialItems.push({
        platform: 'Twitter',
        value: contactAny.twitter || contactAny.twitterUsername,
        icon: 'logo-twitter',
      });
    }
    if (contactAny.linkedin || contactAny.linkedinUrl) {
      socialItems.push({
        platform: 'LinkedIn',
        value: contactAny.linkedin || contactAny.linkedinUrl,
        icon: 'logo-linkedin',
      });
    }

    // Check enrichment.socialProfiles
    const socialProfiles = contactAny.enrichment?.socialProfiles || {};
    Object.entries(socialProfiles).forEach(([platform, value]) => {
      if (value && typeof value === 'string') {
        const iconMap: Record<string, string> = {
          facebook: 'logo-facebook',
          instagram: 'logo-instagram',
          twitter: 'logo-twitter',
          linkedin: 'logo-linkedin',
          whatsapp: 'logo-whatsapp',
          youtube: 'logo-youtube',
          tiktok: 'logo-tiktok',
        };
        socialItems.push({
          platform: platform.charAt(0).toUpperCase() + platform.slice(1),
          value,
          icon: iconMap[platform.toLowerCase()] || 'share-social-outline',
        });
      }
    });

    // Check channels.social
    const socialChannels = contactAny.channels?.social || [];
    if (Array.isArray(socialChannels)) {
      socialChannels.forEach((channel: any) => {
        if (channel?.platform && (channel?.username || channel?.socialId)) {
          const iconMap: Record<string, string> = {
            facebook: 'logo-facebook',
            instagram: 'logo-instagram',
            twitter: 'logo-twitter',
            linkedin: 'logo-linkedin',
            whatsapp: 'logo-whatsapp',
            youtube: 'logo-youtube',
            tiktok: 'logo-tiktok',
          };
          socialItems.push({
            platform: channel.platform.charAt(0).toUpperCase() + channel.platform.slice(1),
            value: channel.username || channel.socialId,
            icon: iconMap[channel.platform.toLowerCase()] || 'share-social-outline',
          });
        }
      });
    }

    return socialItems;
  };

  // Check if there's an ongoing order (not delivered or cancelled)
  // Ongoing statuses: pending, placed, confirmed, processing, shipped
  const hasOngoingOrder = previousOrders.some((order: any) => {
    const status = order.orderStatus?.toLowerCase();
    return status && status !== 'delivered' && status !== 'cancelled';
  });

  const socialItems = contact ? getSocialMediaItems() : [];

  return (
    <View style={styles.bigContainer}>
      <SimpleView contact={contact} mode={mode} onAddNewContact={onAddNewContact} {...simpleProps} />
      
      {contact && (
        <View style={styles.bigDetailsContainer}>
          {/* Left Column - Contact Information */}
          <View style={styles.bigDetailsLeft}>
            {/* Admin mode edit controls */}
            {mode === 'admin' && !isEditing && (
              <TouchableOpacity onPress={onEdit} style={styles.editButton}>
                <Ionicons name="create-outline" size={18} color={colors.primary} />
                <Text style={styles.editButtonText}>Edit Contact</Text>
              </TouchableOpacity>
            )}

            {isEditing ? (
              <View style={styles.editForm}>
                <TextInput
                  style={styles.editInput}
                  value={editingContact.name || ''}
                  onChangeText={(text) => onEditChange({ name: text })}
                  placeholder="Name"
                />
                <TextInput
                  style={styles.editInput}
                  value={editingContact.phoneNumber || editingContact.phone || ''}
                  onChangeText={(text) => onEditChange({ phoneNumber: text, phone: text })}
                  placeholder="Phone"
                  keyboardType="phone-pad"
                />
                <TextInput
                  style={styles.editInput}
                  value={editingContact.email || ''}
                  onChangeText={(text) => onEditChange({ email: text })}
                  placeholder="Email"
                  keyboardType="email-address"
                />
                <View style={styles.editActions}>
                  <TouchableOpacity onPress={onCancel} style={styles.cancelButton}>
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={onSave} style={styles.saveButton} disabled={updating}>
                    {updating ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Text style={styles.saveButtonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <>
                {/* Email */}
                {contact.email && (
                  <View style={styles.infoRow}>
                    <Ionicons name="mail-outline" size={20} color={colors.primary} />
                    <Text style={styles.infoText}>{contact.email}</Text>
                  </View>
                )}

                {/* Address */}
                {contact.address && (
                  <View style={styles.infoRow}>
                    <Ionicons name="location-outline" size={20} color={colors.primary} />
                    <Text style={styles.infoText}>{contact.address}</Text>
                  </View>
                )}

                {/* Location (if available) */}
                {contact.location && contact.location.address && (
                  <View style={styles.infoRow}>
                    <Ionicons name="map-outline" size={20} color={colors.primary} />
                    <View style={styles.infoColumn}>
                      <Text style={styles.infoText}>{contact.location.address}</Text>
                      {contact.location.latitude && contact.location.longitude && (
                        <Text style={styles.infoSubtext}>
                          {contact.location.latitude.toFixed(6)}, {contact.location.longitude.toFixed(6)}
                        </Text>
                      )}
                    </View>
                  </View>
                )}
              </>
            )}
          </View>

          {/* Right Column - Statistics */}
          <View style={styles.bigDetailsRight}>
            {/* Previous Orders */}
            <View style={styles.statItemCompact}>
              <Ionicons name="receipt-outline" size={16} color={colors.primary} />
              {loadingHistory ? (
                <ActivityIndicator size="small" color={colors.primary} style={styles.statLoader} />
              ) : (
                <Text style={styles.statTextCompact}>{previousOrders.length} Orders</Text>
              )}
            </View>

            {/* Previous Events */}
            <View style={styles.statItemCompact}>
              <Ionicons name="calendar-outline" size={16} color={colors.primary} />
              {loadingHistory ? (
                <ActivityIndicator size="small" color={colors.primary} style={styles.statLoader} />
              ) : (
                <Text style={styles.statTextCompact}>{previousEvents.length} Events</Text>
              )}
            </View>

            {/* Has Ongoing Order */}
            <View style={styles.statItemCompact}>
              <Ionicons 
                name={hasOngoingOrder ? "checkmark-circle" : "close-circle"} 
                size={16} 
                color={hasOngoingOrder ? colors.success : colors.textSecondary} 
              />
              <Text style={[styles.statTextCompact, { color: hasOngoingOrder ? colors.success : colors.textSecondary }]}>
                {hasOngoingOrder ? 'Ongoing Order' : 'No Active Order'}
              </Text>
            </View>

            {/* Social Media Links */}
            <View style={styles.statItemCompact}>
              <Ionicons name="share-social-outline" size={16} color={colors.primary} />
              <Text style={styles.statTextCompact}>{socialItems.length} Social Links</Text>
            </View>

            {/* Address */}
            {contact.address || contact.location?.address ? (
              <View style={styles.statItemCompact}>
                <Ionicons name="location-outline" size={16} color={colors.primary} />
                <Text style={styles.statTextCompact} numberOfLines={1}>
                  {contact.address || contact.location?.address}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  simpleContainer: {
    alignSelf: 'flex-start',
    width: 'auto',
    minWidth: 280,
    position: 'relative',
    zIndex: 99998,
    elevation: 50,
    overflow: 'visible' as any,
    ...(Platform.OS === 'web' && {
      pointerEvents: 'auto',
      zIndex: 999999,
    }),
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  contactAvatar: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactAvatarImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  contactAvatarPlaceholder: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactAvatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  contactInfoColumn: {
    flex: 1,
    justifyContent: 'center',
    minHeight: 48,
  },
  contactInfoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 48,
  },
  contactInfoContent: {
    flex: 1,
    justifyContent: 'center',
    gap: spacing.xs / 2,
    minHeight: 48,
  },
  contactNameWrapper: {
    minHeight: 24,
  },
  contactNameButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    minHeight: 24,
  },
  contactNameContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  contactName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  contactPlaceholder: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  contactActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginLeft: spacing.xs,
  },
  removeContactButton: {
    padding: spacing.xs / 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactChevron: {
    alignSelf: 'center',
  },
  phoneInputContainer: {
    overflow: 'hidden',
    width: '100%',
    maxWidth: 280,
  },
  phoneInputContainerStyle: {
    marginBottom: 0,
  },
  phoneInput: {
    width: '100%',
  },
  dropdownWrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    marginTop: spacing.xs,
    width: '100%',
    zIndex: 99999,
    elevation: 50,
    ...(Platform.OS === 'web' && {
      position: 'absolute' as any,
      zIndex: 9999999,
    }),
  },
  dropdownContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    width: '100%',
    maxHeight: 500,
    overflow: 'hidden',
    shadowColor: colors.textDark,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    }),
  },
  dropdownContent: {
    maxHeight: 500,
  },
  contactSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: spacing.md,
    paddingHorizontal: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.lightGray,
  },
  contactSearchIcon: {
    marginRight: spacing.xs,
  },
  contactSearchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    paddingVertical: spacing.xs,
    fontFamily: typography.fontFamily.regular,
  },
  addContactButton: {
    padding: spacing.xs,
    marginLeft: spacing.xs,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactList: {
    maxHeight: 300,
  },
  contactOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.25),
  },
  contactOptionAvatar: {
    marginRight: spacing.md,
  },
  contactOptionAvatarImage: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 2,
    borderColor: colors.background.surface,
  },
  contactOptionAvatarPlaceholder: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactOptionAvatarText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  contactOptionInfo: {
    flex: 1,
  },
  contactOptionName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.textDark,
    marginBottom: spacing.xs / 2,
    fontFamily: typography.fontFamily.medium,
  },
  contactOptionPhone: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  contactEmpty: {
    padding: spacing.xl * 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactEmptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
  },
  // Inline styles
  inlineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  inlineAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  inlineAvatarPlaceholder: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  inlineAvatarText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  inlineName: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  inlinePhone: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  inlineEditButton: {
    marginLeft: 'auto',
    padding: spacing.xs,
  },
  // Big view styles
  bigContainer: {
    width: '100%',
    position: 'relative',
    zIndex: 1,
    ...(Platform.OS === 'web' && {
      overflow: 'visible' as any,
      zIndex: 999998,
    }),
  },
  bigDetailsContainer: {
    marginTop: spacing.md,
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: spacing.md,
    alignItems: 'stretch', // Ensure both columns have same height
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 1,
    }),
  },
  bigDetailsLeft: {
    flex: 1,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    ...designTokens.shadows.sm,
    alignSelf: 'stretch', // Stretch to match container height
    ...(Platform.OS !== 'web' && {
      width: '100%',
    }),
  },
  bigDetailsRight: {
    width: Platform.OS === 'web' ? 220 : '100%',
    padding: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    ...designTokens.shadows.sm,
    alignSelf: 'stretch', // Stretch to match container height
    ...(Platform.OS === 'web' && {
      minWidth: 220,
      maxWidth: 220,
    }),
  },
  bigDetails: {
    marginTop: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    ...designTokens.shadows.sm,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 1,
    }),
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    padding: spacing.sm,
    marginBottom: spacing.md,
    alignSelf: 'flex-start',
  },
  editButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  editForm: {
    gap: spacing.md,
  },
  editInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  editActions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  cancelButton: {
    flex: 1,
    padding: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  saveButton: {
    flex: 1,
    padding: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.white,
    fontFamily: typography.fontFamily.medium,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  infoColumn: {
    flex: 1,
    minWidth: 0,
  },
  infoText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  infoSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.xs / 2,
    fontFamily: typography.fontFamily.regular,
  },
  historySection: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  historyTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textDark,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  historyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  historyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  loadingHistory: {
    marginTop: spacing.md,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontStyle: 'italic',
    fontFamily: typography.fontFamily.regular,
  },
  statItemCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.25),
  },
  statTextCompact: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    flex: 1,
  },
  statLoader: {
    marginLeft: spacing.xs,
  },
});
